import { until } from '@vueuse/core';
import { ref } from 'vue';
import { requirejs } from '~/modules/require';
import type { DS_Usage_TrackerAPI } from '~/typings/Usage';
type CallbackFunction = (options: any) => void;

let api: any;
let isTrackerLoading = false;
const TrackerInitComplete = ref(false);

export const useTracker = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const wait = async () =>
    new Promise((resolve) => {
      setTimeout(resolve, 2000);
    });
  const promise = new Promise(async () => {
    if (api || isTrackerLoading) {
      return;
    }

    isTrackerLoading = true;
    await wait();
    const [TrackerAPI, UsageManager] = await requirejs<[DS_Usage_TrackerAPI, any]>([
      'DS/Usage/TrackerAPI',
      'DS/Usage/UsageManager',
    ]);

    let user;
    if (window.dsUserLogin) {
      user = { login: window.dsUserLogin };
    }

    UsageManager.init({ uploadPath: '/upload', service: '3dprint', user });
    TrackerAPI.init();
    api = TrackerAPI;
    TrackerInitComplete.value = true;
  });

  const convertPers = (type: string, pers: string[] | number[]) => {
    const values: Record<string, unknown> = {};

    let index;

    if (!pers.length) {
      return undefined;
    }

    for (index = 0; index < pers.length; index += 1) {
      if (pers[index]) {
        if (type === 'pd' && typeof pers[index] !== 'string') {
          try {
            pers[index] = pers[index].toString();
          } catch (e) {
            throw new Error(`Value ${pers[index]} for pv${index + 1} 
						is not a string and is not convertible`);
          }
        }

        if (type === 'pv' && typeof pers[index] !== 'number') {
          try {
            pers[index] = Number.parseFloat(pers[index] as string);
          } catch (e) {
            throw new Error(`Value ${pers[index]} for pv${index + 1} 
						is not a number and is not convertible`);
          }
        }

        values[type + (index + 1)] = pers[index];
      }
    }

    return values;
  };

  const event = async (
    label: string,
    category: string,
    action: string,
    persDim: string[] = [],
    persVal: number[] = [],
    callback: CallbackFunction = () => {
      /* do nothing */
    },
  ) => {
    const dim = Array.isArray(persDim) ? convertPers('pd', persDim) : persDim;
    const val = Array.isArray(persVal) ? convertPers('pv', persVal) : persVal;

    await until(TrackerInitComplete).toBe(true);

    if (!api?.trackPageEvent) {
      return;
    }

    api.trackPageEvent({
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      persDim: dim,
      persVal: val,
      callback,
    });
  };

  const error = async (
    category: string,
    errorLevel: string,
    errorMessage: string,
    errorName: string,
    persDim: string[] = [],
    persVal: number[] = [],
  ) => {
    const dim = Array.isArray(persDim) ? convertPers('pd', persDim) : persDim;
    const val = Array.isArray(persVal) ? convertPers('pv', persVal) : persVal;

    await until(TrackerInitComplete).toBe(true);

    if (!api?.trackPageError) {
      return;
    }

    api.trackPageError({
      errorCategory: category,
      errorLevel,
      errorMessage,
      errorSource: document.location.pathname,
      errorName,
      persDim: dim,
      persVal: val,
    });
  };

  const trackPage = async (name: string) => {
    await until(TrackerInitComplete).toBe(true);
    if (!api?.trackPageEvent) {
      return;
    }

    api.trackPageView(document.location.pathname, document.title, { pd1: name });
  };

  return {
    event,
    error,
    promise,
    trackPage,
  };
};
